import { authorizedApiClient } from "../config";

export interface RecommendedSignalConfig {
  name: string;
  description: string;
  trigger: "usage_based" | "event_based";
  entity_relation: "accounts" | "users";
  automation_type: "signal";
  filters: {
    query_type: "OR";
    predicates: Array<{
      query_type: "AND";
      predicates: Array<{
        field: string;
        operator: string;
        value: string | number | boolean | { start: string; end: string };
        type: string;
        entity_type: string;
        time_window?: string;
      }>;
    }>;
  };
  metrics?: string[];
  sort: {
    field: string;
    order: "asc" | "desc";
    type: string;
  };
  selected_fields: string[];
  automation_actions: {
    actions: Array<{
      type: "send_to_inbox" | "send_to_slack" | "send_to_email";
      assign_to: string;
      assign_to_key: string;
      channel_id?: string;
      slack_user_id?: string;
      message_type?: "channel" | "direct";
      message?: string;
      to?: string;
      subject?: string;
      body?: string;
    }>;
    suppression_period: "all_time" | "daily";
    run_schedule: string;
    playbook_id?: string;
  };
}

export interface RecommendedSignal {
  id: string;
  title: string;
  description: string;
  entity_type: "accounts" | "users";
  category: "engagement" | "churn" | "expansion" | "health" | "onboarding" | "billing";
  popular?: boolean;
  signal_config: RecommendedSignalConfig;
}

export interface RecommendedSignalsResponse {
  recommended_signals: RecommendedSignal[];
  total: number;
  generated_at: string;
}

export interface GetRecommendedSignalsParams {
  workspaceId: string;
  entityType?: "accounts" | "users";
  limit?: number;
}

// Fallback static data in case API fails or is not yet ready
const STATIC_RECOMMENDED_SIGNALS: RecommendedSignal[] = [
  {
    id: "sig_high_intent",
    title: "High Intent Accounts",
    description: "Identify accounts showing high engagement signals but haven't converted yet.",
    entity_type: "accounts",
    category: "engagement",
    popular: true,
    signal_config: {
      name: "High Intent Accounts",
      description: "Accounts with high activity in the last 7 days",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "accounts.c__total_active_users_7d",
              operator: "gt",
              value: 5,
              type: "int",
              entity_type: "column"
            }
          ]
        }]
      },
      sort: {
        field: "accounts.c__total_active_users_7d",
        order: "desc",
        type: "property"
      },
      selected_fields: ["accounts.name", "accounts.created_at"],
      automation_actions: {
        actions: [],
        suppression_period: "daily",
        run_schedule: "0 9 * * *"
      }
    }
  },
  {
    id: "sig_churn_risk",
    title: "Churn Risk Detection",
    description: "Spot accounts with declining usage patterns before they churn.",
    entity_type: "accounts",
    category: "churn",
    popular: true,
    signal_config: {
      name: "Churn Risk Alert",
      description: "Accounts with low recent activity",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "accounts.c__total_active_users_30d",
              operator: "lt",
              value: 2,
              type: "int",
              entity_type: "column"
            }
          ]
        }]
      },
      sort: {
        field: "accounts.c__total_active_users_30d",
        order: "asc",
        type: "property"
      },
      selected_fields: ["accounts.name", "accounts.last_seen"],
      automation_actions: {
        actions: [],
        suppression_period: "daily",
        run_schedule: "0 9 * * *"
      }
    }
  },
  {
    id: "sig_new_power_users",
    title: "New Power Users",
    description: "Identify new users who are adopting key features rapidly.",
    entity_type: "users",
    category: "onboarding",
    signal_config: {
      name: "New Power Users",
      description: "New users with high activity",
      trigger: "usage_based",
      entity_relation: "users",
      automation_type: "signal",
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "users.created_at",
              operator: "gt",
              value: "$now-14d",
              type: "timestamp",
              entity_type: "column"
            },
            {
              field: "users.c__days_active_7d",
              operator: "gt",
              value: 3,
              type: "int",
              entity_type: "column"
            }
          ]
        }]
      },
      sort: {
        field: "users.c__days_active_7d",
        order: "desc",
        type: "property"
      },
      selected_fields: ["users.name", "users.email", "users.created_at"],
      automation_actions: {
        actions: [],
        suppression_period: "daily",
        run_schedule: "0 9 * * *"
      }
    }
  },
  {
    id: "sig_expansion_opp",
    title: "Expansion Opportunity",
    description: "Find accounts approaching their usage limits or showing growth.",
    entity_type: "accounts",
    category: "expansion",
    signal_config: {
      name: "Expansion Opportunity",
      description: "Accounts with growing user base",
      trigger: "usage_based",
      entity_relation: "accounts",
      automation_type: "signal",
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "accounts.c__total_active_users_30d",
              operator: "gt",
              value: 10,
              type: "int",
              entity_type: "column"
            }
          ]
        }]
      },
      sort: {
        field: "accounts.c__total_active_users_30d",
        order: "desc",
        type: "property"
      },
      selected_fields: ["accounts.name", "accounts.plan"],
      automation_actions: {
        actions: [],
        suppression_period: "daily",
        run_schedule: "0 9 * * *"
      }
    }
  },
  {
    id: "sig_inactive_admin",
    title: "Inactive Admins",
    description: "Flag accounts where key decision makers (admins) have stopped logging in.",
    entity_type: "users",
    category: "health",
    signal_config: {
      name: "Inactive Admins",
      description: "Admins inactive for 14+ days",
      trigger: "usage_based",
      entity_relation: "users",
      automation_type: "signal",
      filters: {
        query_type: "OR",
        predicates: [{
          query_type: "AND",
          predicates: [
            {
              field: "users.role",
              operator: "eq",
              value: "admin",
              type: "string",
              entity_type: "column"
            },
            {
              field: "users.last_seen",
              operator: "lt",
              value: "$now-14d",
              type: "timestamp",
              entity_type: "column"
            }
          ]
        }]
      },
      sort: {
        field: "users.last_seen",
        order: "asc",
        type: "property"
      },
      selected_fields: ["users.name", "users.email", "users.last_seen"],
      automation_actions: {
        actions: [],
        suppression_period: "daily",
        run_schedule: "0 9 * * *"
      }
    }
  }
];

export const getRecommendedSignals = async ({
  workspaceId,
  entityType,
  limit = 10,
}: GetRecommendedSignalsParams): Promise<RecommendedSignalsResponse> => {
  try {
  const params: Record<string, string | number> = { limit };
  if (entityType) {
    params.entity_type = entityType;
  }

  const response = await authorizedApiClient.get<RecommendedSignalsResponse>(
    `/workspace/${workspaceId}/signals/recommended`,
    {
      params,
      headers: {
        "X-Workspace-Id": workspaceId,
      },
    }
  );

  return response.data;
  } catch (error) {
    console.error("Failed to fetch recommended signals, using fallback data:", error);
    
    // Fallback logic
    let signals = STATIC_RECOMMENDED_SIGNALS;
    
    if (entityType) {
      signals = signals.filter(s => s.entity_type === entityType);
    }
    
    if (limit) {
      signals = signals.slice(0, limit);
    }

    return {
      recommended_signals: signals,
      total: signals.length,
      generated_at: new Date().toISOString()
    };
  }
};
